import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const STRINGS = {
  en: {
    banner: "FREE SHIPPING ON ALL ORDERS FOR THE US & CANADA",
    nav: { create: "Create", admin: "Admin", orders: "Orders", designs: "My designs", login: "Sign in", logout: "Logout" },
    cart: {
      title: "Your cart",
      empty: "Your cart is empty.",
      startDesigning: "Start designing",
      pcs: "pcs",
      each: "ea",
      sheet: "sheet",
      sheets: "sheets",
      stickers: "stickers",
      remove: "Remove",
      summary: "Order summary",
      items: "Items",
      shipTo: "Ship to",
      country: "Country",
      province: "Province",
      state: "State",
      selectProvince: "Select province",
      selectState: "Select state",
      canada: "Canada",
      usa: "United States",
      subtotal: "Subtotal",
      shipping: "Shipping",
      free: "FREE",
      total: "Total",
      checkout: "Checkout",
      securePay: "Secure payment via Stripe",
      shipTo: "Contact & shipping", fullName: "Full name", email: "Email", phone: "Phone (optional)", address1: "Street address", address2: "Apartment, suite (optional)", city: "City", postal: "Postal / ZIP code",
      signInToCheckout: "Please sign in to checkout",
      taxNote: "Taxes are calculated based on your shipping destination.",
      noTaxUS: "No sales tax applies to US orders.",
      chooseDestination: "Choose your destination to see taxes.",
      shipsBy: "Ships by",
      savedAddresses: "Saved addresses",
      chooseSaved: "Use a saved address…",
      saveThisAddress: "Save this address",
      addressLabelPh: "Label (e.g. Home, Work)",
      addressSaved: "Address saved",
      addressDeleted: "Address removed",
      defaultSet: "Default address updated",
      defaultBadge: "Default",
      makeDefault: "Set default",
    },
    success: {
      checking: "Confirming payment…",
      checkingSub: "Hang tight, this only takes a moment.",
      paidTitle: "Order confirmed!",
      paidSub: "Your stickers are heading to production. 🎉",
      shippingTo: "Shipping to",
      viewOrders: "View orders",
      designMore: "Design more",
      timeoutTitle: "Still processing",
      timeoutMsg: "Check your Orders page shortly for the latest status.",
      failedTitle: "Payment not completed",
      failedMsg: "Your payment was cancelled or failed. You can try again.",
      backToCart: "Back to cart",
    },
    orders: {
      title: "Your orders",
      signIn: "Please sign in to view your orders.",
      signInBtn: "Sign in",
      empty: "No orders yet.",
      create: "Create a sticker",
      order: "Order",
      pcs: "pcs",
      st_paid: "paid", st_pending: "pending", st_expired: "expired",
    },
    designer: {
      title: "Design your sticker",
      subtitle: "Upload a PNG — We auto-trace your shape & add a contour.",
      surface: "Surface", inContext: "In context",
      laptop: "Laptop", notebook: "Notebook", bottle: "Bottle", phone: "Phone",
      tracing: "Tracing your artwork…",
      dropHere: "Drop your PNG here", browse: "or click to browse · max 15MB",
      replace: "Replace image",
      material: "Material", cutType: "Cut type",
      kissDesc: "On a backing liner", sheetDesc: "Nested on 8.5\"×11\"", dieDesc: "Cut to your shape",
      nestPre: "nest per 8.5\"×11\" sheet", nestSticker: "sticker", nestStickers: "stickers",
      only1: "Only 1 fits —", useKissInstead: "use Kiss Cut instead", tooLarge: "Sticker too large to nest —", useKiss: "use Kiss Cut",
      printedSize: "Printed image size (in)",
      ratioLocked: "Ratio locked", freeRatio: "Free ratio",
      presetsNote: "Presets keep your artwork ratio",
      showFewerSizes: "Show fewer sizes", viewMoreSizes: "View more sizes →",
      width: "Width", height: "Height",
      actualSize: "Actual sticker size:", cutBorder: "cut border", imageWord: "image",
      shipOrderIn: "Order in the next", shipGetBy: "to ship it by",
      lowResWarn: "Heads up — your artwork is only", lowResTail: "at this size. It may look blurry in print. For crisp results, use a higher-resolution PNG or a smaller size.",
      sheets: "Sheets", quantity: "Quantity",
      showFewer: "Show fewer", showMore: "Show more",
      sheetWord: "sheet", sheetsWord: "sheets", stickersTotal: "stickers", totalWord: "total",
      save: "HEY! SAVE", bumpTo: "Bump to", pcs: "pcs", forWord: "for", off: "off", tapApply: "Tap to apply!",
      addMessage: "Add a message",
      msgPlaceholder: "Special instructions, notes for our print team, or a gift message…",
      total: "Total", perSticker: "/ sticker", perSheet: "/sheet", bulkOff: "bulk off",
      addToCart: "Add to cart", finalPrice: "Final price confirmed securely at checkout.",
      added: "Added to cart!",
      shapeTitle: "Shape", shapeContour: "Contour Cut", shapeRect: "Rectangle", shapeCircle: "Circle",
      offsetTitle: "Sticker Offset", offsetHint: "Border around your artwork. Minimum 1/16\", default 1/8\".",
      faqTitle: "Questions? We've got answers",
      seoTitle: "Design Your Custom Stickers Online | 2TSticker",
      seoDesc: "Upload your artwork and design custom die-cut, kiss-cut or sheet stickers. Live pricing, instant preview and fast Canadian production.",
      whiteInk: "Add white ink underbase", whiteInkHint: "Boosts opacity & vibrancy on clear, chrome & holographic.",
      upTitle: "Best-value upgrade", upGetA: "Get", upMoreItems: "more", upStickersWord: "stickers", upSheetsWord: "sheets",
      upForOnly: "for only", upMoreCost: "more.", upFromWord: "Upgrade from", upToWord: "to",
      upSaveWord: "and save", upPerSticker: "per sticker", upPerSheet: "per sheet",
      upBtn: "Upgrade to", upKeep: "Keep",
    },
    landing: {
      badge: "Custom die-cut, kiss-cut & sheet stickers",
      heroPre: "Custom Stickers Printed in ",
      heroAccent: "Canada",
      heroSub: "Upload your artwork and watch it become a real, vinyl sticker — instant preview, instant pricing, vibrant colors, amazing quality. Choose white vinyl, transparent, chrome or holographic.",
      shopByType: "Shop by Sticker Type",
      pWhite: "Custom white vinyl stickers are printed in full colour on an opaque white base, so your artwork stays bright and accurate on any surface. Perfect for product labels, logo stickers and business branding, plus laptops, bottles and packaging. The solid white background keeps light colours and fine detail crisp. Upload a PNG, preview instantly, choose your cut and size, and order any quantity.",
      pClear: "Custom transparent stickers print on clear vinyl so only your artwork shows and the background disappears. Ideal for windows, bottles, jars and clear packaging on both light and dark surfaces. They give products a clean, label-free, high-end look. Design with bold shapes and solid colours for the sharpest result, then preview the clear finish live before you order.",
      pChrome: "Custom chrome stickers deliver a bright, reflective metallic silver finish that grabs attention. The mirror-like chrome look adds a premium feel that flat inks can't match — great for premium product labels, automotive branding and eye-catching designs. Printed colours sit over the reflective base for striking logos. Preview the metallic effect, choose your cut and size, and order online.",
      pHolo: "Custom holographic stickers feature a rainbow reflective, colour-shifting finish that sparkles as the angle changes. This eye-catching effect is perfect for packaging, promotional stickers, event swag and creative branding. The shifting shimmer sits beneath your artwork for depth and a playful, premium feel. Bold designs let the holographic shine come through — preview it live and order in any run size.",
      pOrderCta: "Order now",
      faqTitle: "Frequently asked questions",
      faqSub: "Everything you need to know before you order.",
      start: "Start creating",
      how: "How it works",
      f1: "Waterproof vinyl", f2: "Gloss laminated", f3: "Fade resistant",
      fromPrice: "from $0.15/ea",
      howTitle: "Four steps to sticker heaven",
      howSub: "No design software. No cut-line files. We handle the tricky bits.",
      s1t: "Upload PNG", s1x: "Drop your transparent PNG artwork.",
      s2t: "Auto cut-line", s2x: "We trace your shape & add a contour.",
      s3t: "Pick material", s3x: "White, Transparent, Chrome or Holographic vinyl.",
      s4t: "Order & ship", s4x: "Checkout securely and we print & deliver.",
      matTitle: "Four premium finishes",
      matSub: "Every material is weatherproof and made to last for years.",
      m1t: "White Vinyl", m1x: "Bold, opaque & vivid. The everyday classic.",
      m2t: "Transparent Vinyl", m2x: "See-through backing that blends into any surface.",
      m3t: "Holographic Vinyl", m3x: "Rainbow shimmer that catches every angle.",
      mCt: "Chrome Vinyl", mCx: "Mirror-metallic silver finish with a reflective shine.",
      bulkTitle: "The more you order, the more you save", bulkSub: "Every quantity is priced independently — bigger runs mean a lower price per sticker.", bulkQty: "stickers", bulkCta: "Start saving",
      bulkColQty: "Quantity", bulkColEach: "Price / sticker", bulkColSave: "You save", bulkSample: "Sample pricing for a", bulkPopular: "Popular",
      seoTitle: "Custom Stickers Canada | 2TSticker",
      seoDesc: "Order custom white vinyl, transparent, chrome and holographic stickers online. Durable, waterproof die-cut printing with easy PNG upload and fast Canadian service.",
      designCta: "Design your sticker",
      rights: "All rights reserved. Printed with love & rainbows.",
    },
    common: { cad: "CAD", support: "Customer Service" },
  },
  fr: {
    banner: "LIVRAISON GRATUITE POUR TOUTES LES COMMANDES AUX ÉTATS-UNIS ET AU CANADA",
    nav: { create: "Créer", admin: "Admin", orders: "Commandes", designs: "Mes designs", login: "Connexion", logout: "Déconnexion" },
    cart: {
      title: "Votre panier",
      empty: "Votre panier est vide.",
      startDesigning: "Commencer à créer",
      pcs: "unités",
      each: "ch",
      sheet: "feuille",
      sheets: "feuilles",
      stickers: "autocollants",
      remove: "Retirer",
      summary: "Récapitulatif",
      items: "Articles",
      shipTo: "Livrer à",
      country: "Pays",
      province: "Province",
      state: "État",
      selectProvince: "Choisir une province",
      selectState: "Choisir un état",
      canada: "Canada",
      usa: "États-Unis",
      subtotal: "Sous-total",
      shipping: "Livraison",
      free: "GRATUITE",
      total: "Total",
      checkout: "Passer à la caisse",
      securePay: "Paiement sécurisé via Stripe",
      shipTo: "Contact et livraison", fullName: "Nom complet", email: "Courriel", phone: "Téléphone (facultatif)", address1: "Adresse", address2: "Appartement, bureau (facultatif)", city: "Ville", postal: "Code postal",
      signInToCheckout: "Veuillez vous connecter pour payer",
      taxNote: "Les taxes sont calculées selon votre destination de livraison.",
      noTaxUS: "Aucune taxe de vente ne s'applique aux commandes américaines.",
      chooseDestination: "Choisissez votre destination pour voir les taxes.",
      shipsBy: "Envoi le",
      savedAddresses: "Adresses enregistrées",
      chooseSaved: "Utiliser une adresse enregistrée…",
      saveThisAddress: "Enregistrer cette adresse",
      addressLabelPh: "Étiquette (ex. Maison, Travail)",
      addressSaved: "Adresse enregistrée",
      addressDeleted: "Adresse supprimée",
      defaultSet: "Adresse par défaut mise à jour",
      defaultBadge: "Par défaut",
      makeDefault: "Définir par défaut",
    },
    success: {
      checking: "Confirmation du paiement…",
      checkingSub: "Un instant, ça ne prend qu'un moment.",
      paidTitle: "Commande confirmée !",
      paidSub: "Vos autocollants partent en production. 🎉",
      shippingTo: "Livraison à",
      viewOrders: "Voir les commandes",
      designMore: "Créer plus",
      timeoutTitle: "Traitement en cours",
      timeoutMsg: "Consultez bientôt votre page Commandes pour le statut.",
      failedTitle: "Paiement non complété",
      failedMsg: "Votre paiement a été annulé ou a échoué. Vous pouvez réessayer.",
      backToCart: "Retour au panier",
    },
    orders: {
      title: "Vos commandes",
      signIn: "Veuillez vous connecter pour voir vos commandes.",
      signInBtn: "Connexion",
      empty: "Aucune commande pour l'instant.",
      create: "Créer un autocollant",
      order: "Commande",
      pcs: "unités",
      st_paid: "payée", st_pending: "en attente", st_expired: "expirée",
    },
    designer: {
      title: "Créez votre autocollant",
      subtitle: "Téléversez un PNG — nous traçons votre forme et ajoutons un contour.",
      dropHere: "Déposez votre PNG ici",
      browse: "ou cliquez pour parcourir · max 15 Mo",
      replace: "Remplacer l'image",
      surface: "Surface",
      inContext: "En contexte",
      material: "Matériau",
      cutType: "Type de coupe",
      printedSize: "Taille imprimée (po)",
      ratioLocked: "Ratio verrouillé",
      width: "Largeur",
      height: "Hauteur",
      quantity: "Quantité",
      shipOrderIn: "Commandez d'ici", shipGetBy: "pour un envoi le",
      lowResWarn: "Attention — votre visuel n'est qu'à", lowResTail: "à cette taille. L'impression pourrait être floue. Pour un rendu net, utilisez un PNG haute résolution ou une taille plus petite.",
      addToCart: "Ajouter au panier",
      viewMoreSizes: "Voir plus de tailles →",
      shapeTitle: "Forme", shapeContour: "Découpe contour", shapeRect: "Rectangle", shapeCircle: "Cercle",
      offsetTitle: "Décalage de l'autocollant", offsetHint: "Bordure autour de votre visuel. Minimum 1/16 po, défaut 1/8 po.",
      faqTitle: "Des questions? Voici les réponses",
      seoTitle: "Concevez vos autocollants personnalisés en ligne | 2TSticker",
      seoDesc: "Téléversez votre visuel et créez des autocollants découpés, kiss-cut ou en planche. Prix en direct, aperçu instantané et production canadienne rapide.",
      whiteInk: "Ajouter une sous-couche d'encre blanche", whiteInkHint: "Augmente l'opacité et l'éclat sur transparent, chrome et holographique.",
      upTitle: "Meilleure offre", upGetA: "Obtenez", upMoreItems: "de plus", upStickersWord: "autocollants", upSheetsWord: "planches",
      upForOnly: "pour seulement", upMoreCost: "de plus.", upFromWord: "Passez de", upToWord: "à",
      upSaveWord: "et économisez", upPerSticker: "par autocollant", upPerSheet: "par planche",
      upBtn: "Passer à", upKeep: "Garder",
    },
    landing: {
      badge: "Autocollants découpés, kiss-cut & en planche sur mesure",
      heroPre: "Autocollants personnalisés imprimés au ",
      heroAccent: "Canada",
      heroSub: "Téléversez votre visuel et voyez-le devenir un véritable autocollant vinyle — aperçu et prix instantanés, couleurs éclatantes, qualité incroyable. Choisissez vinyle blanc, transparent, chrome ou holographique.",
      shopByType: "Magasiner par type d'autocollant",
      pWhite: "Les autocollants en vinyle blanc personnalisés sont imprimés en couleur sur un fond blanc opaque, gardant votre visuel vif et fidèle sur toute surface. Parfaits pour les étiquettes de produits, les logos et l'image de marque, ainsi que les portables, bouteilles et emballages. Le fond blanc conserve les couleurs claires et les détails fins. Téléversez un PNG, prévisualisez, choisissez la découpe et la taille, et commandez.",
      pClear: "Les autocollants transparents personnalisés sont imprimés sur vinyle clair : seul votre visuel apparaît, le fond disparaît. Idéaux pour vitres, bouteilles, pots et emballages transparents, sur surfaces claires ou foncées. Ils donnent un look épuré et haut de gamme. Privilégiez des formes marquées et des couleurs pleines, puis prévisualisez le fini clair avant de commander.",
      pChrome: "Les autocollants chrome personnalisés offrent un fini argent métallisé réfléchissant qui attire l'œil. L'effet miroir ajoute une touche premium que les encres mates n'égalent pas — idéal pour étiquettes haut de gamme, image automobile et designs percutants. Les couleurs imprimées reposent sur la base réfléchissante. Prévisualisez l'effet, choisissez découpe et taille, et commandez en ligne.",
      pHolo: "Les autocollants holographiques personnalisés arborent un fini arc-en-ciel réfléchissant et changeant qui scintille selon l'angle. Effet parfait pour emballages, autocollants promotionnels et image de marque créative. Le chatoiement sous votre visuel ajoute profondeur et allure ludique et premium. Prévisualisez en direct et commandez en petite ou grande quantité.",
      pOrderCta: "Commander",
      faqTitle: "Foire aux questions",
      faqSub: "Tout ce qu'il faut savoir avant de commander.",
      start: "Commencer à créer",
      how: "Comment ça marche",
      f1: "Vinyle imperméable", f2: "Laminé brillant", f3: "Résistant à la décoloration",
      fromPrice: "à partir de 0,15 $/ch",
      howTitle: "Quatre étapes vers le paradis des autocollants",
      howSub: "Aucun logiciel de design. Aucun fichier de découpe. On s'occupe du plus délicat.",
      s1t: "Téléversez un PNG", s1x: "Déposez votre visuel PNG transparent.",
      s2t: "Découpe automatique", s2x: "Nous traçons la forme et ajoutons un contour.",
      s3t: "Choisissez le matériau", s3x: "Vinyle blanc, transparent, chrome ou holographique.",
      s4t: "Commandez & recevez", s4x: "Payez en toute sécurité, on imprime et on livre.",
      matTitle: "Quatre finitions premium",
      matSub: "Chaque matériau est imperméable et conçu pour durer des années.",
      m1t: "Vinyle blanc", m1x: "Vif, opaque et éclatant. Le classique du quotidien.",
      m2t: "Vinyle transparent", m2x: "Support transparent qui se fond sur toute surface.",
      m3t: "Vinyle holographique", m3x: "Un chatoiement arc-en-ciel sous tous les angles.",
      mCt: "Vinyle chrome", mCx: "Fini argent métallisé miroir au reflet brillant.",
      bulkTitle: "Plus vous commandez, plus vous économisez", bulkSub: "Chaque quantité est calculée indépendamment — les grandes séries réduisent le prix par autocollant.", bulkQty: "autocollants", bulkCta: "Commencez à économiser",
      bulkColQty: "Quantité", bulkColEach: "Prix / autocollant", bulkColSave: "Économie", bulkSample: "Prix indicatif pour un", bulkPopular: "Populaire",
      seoTitle: "Autocollants personnalisés au Canada | 2TSticker",
      seoDesc: "Commandez des autocollants personnalisés en vinyle blanc, transparent, chrome et holographique. Impression découpée durable et étanche, téléversement PNG facile, service canadien rapide.",
      designCta: "Créez votre autocollant",
      rights: "Tous droits réservés. Imprimé avec amour et arcs-en-ciel.",
    },
    common: { cad: "CAD", support: "Service à la clientèle" },
  },
};

const LanguageContext = createContext(null);

function detectLang() {
  if (typeof window !== "undefined") {
    const param = new URLSearchParams(window.location.search).get("lang");
    if (param === "en" || param === "fr") return param;
  }
  const saved = typeof window !== "undefined" && localStorage.getItem("lang");
  if (saved === "en" || saved === "fr") return saved;
  const nav = typeof navigator !== "undefined" ? (navigator.language || "en") : "en";
  return nav.toLowerCase().startsWith("fr") ? "fr" : "en";
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang === "fr" ? "fr-CA" : "en-CA";
  }, [lang]);

  const setLang = useCallback((l) => setLangState(l), []);

  const t = useCallback(
    (key) => {
      const parts = key.split(".");
      let cur = STRINGS[lang];
      for (const p of parts) {
        cur = cur?.[p];
        if (cur == null) break;
      }
      if (cur == null) {
        // fallback to English
        let en = STRINGS.en;
        for (const p of parts) en = en?.[p];
        return en ?? key;
      }
      return cur;
    },
    [lang]
  );

  return <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(LanguageContext);
  if (!ctx) return { lang: "en", setLang: () => {}, t: (k) => k };
  return ctx;
}

// Money is always CAD across the app.
export function formatCAD(amount) {
  const n = Number(amount || 0);
  return `$${n.toFixed(2)} CAD`;
}

// Resolve a bilingual content value ({en, fr}) for display, falling back to
// English when the requested language is blank. Accepts legacy plain strings.
export function pickLang(val, lang) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  return val[lang] || val.en || "";
}
